import { useEffect, useRef } from "react";
import type { RequestOptions } from "mixpanel-browser";
import { Mixpanel } from "lib/mixpanel/mixpanel";

import { useUserData } from "lib/store/user";
import { MIXPANEL_TOKEN } from "lib/constants";

/**
 * Options for `track` function for `useMixpanelTracking` hook
 */
export type MixpanelTrackOptions = {
  /**
   * Run tracking only for authorised users
   * @default: true
   */
  withAuth?: boolean;
  sendImmediately?: boolean;
};

export type MixpanelProperties = { [key: string]: any };

export const useMixpanelTracking = () => {
  const mixpanel = useRef<Mixpanel | null>(null);
  const { userId } = useUserData();

  useEffect(() => {
    mixpanel.current = new Mixpanel(MIXPANEL_TOKEN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runTracking = (
    mixpanel: Mixpanel,
    eventName: string,
    properties: MixpanelProperties,
    requestOptions?: RequestOptions
  ) => {
    mixpanel.track(eventName, properties, requestOptions);
  };

  const track = async (
    eventName: string,
    properties: MixpanelProperties,
    options: MixpanelTrackOptions = {}
  ) => {
    const trackingOptions: Required<Pick<MixpanelTrackOptions, "withAuth">> = {
      withAuth: options.withAuth !== undefined ? options.withAuth : true,
    };

    const requestOptions: RequestOptions | undefined = options.sendImmediately
      ? { send_immediately: true, transport: "sendBeacon" }
      : undefined;

    if (mixpanel.current) {
      if (trackingOptions.withAuth) {
        if (userId) {
          runTracking(
            mixpanel.current,
            eventName,
            {
              distinct_id: userId,
              ...properties,
            },
            requestOptions
          );
        }
      } else {
        runTracking(
          mixpanel.current,
          eventName,
          {
            distinct_id: userId,
            ...properties,
          },
          requestOptions
        );
      }
    }
  };

  return { userId, mixpanel, track };
};
